/**
 * Auth + active-tenant client store.
 *
 * Persists to localStorage so a page reload keeps the user signed in until the
 * access token expires (API will respond 401 and the SPA will redirect to /login).
 * Refresh-token rotation lives in an HttpOnly cookie — the API handles that side.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { MemberRole } from '@seo/types';
import { clearNavTrail } from './nav-context';

export interface AuthUser {
  id: string;
  email: string;
  display_name: string | null;
}

export interface AuthTenant {
  id: string;
  name: string;
  slug: string;
  plan: string;
  branding_logo_url?: string | null;
  branding_primary_color?: string | null;
  /**
   * Per-tenant feature-flag map — merged (global | tenant override) by the
   * API's `feature_flags` middleware. Only surfaced when `/v1/me` opts in;
   * treat missing entries as `false` so the frontend fails closed.
   *
   * See `useFeatureFlag` in `lib/feature-flags.ts` for the consumer.
   */
  feature_flags?: Record<string, boolean>;
}

interface AuthState {
  accessToken: string | null;
  tenant: AuthTenant | null;
  user: AuthUser | null;
  role: MemberRole | null;
  signIn: (args: {
    accessToken: string;
    tenant: AuthTenant;
    user: AuthUser;
    role: MemberRole;
  }) => void;
  setTenant: (tenant: AuthTenant) => void;
  setMe: (args: { user: AuthUser; tenant: AuthTenant; role: MemberRole }) => void;
  /** Patch local user fields after a successful PATCH /v1/me. */
  patchUser: (patch: Partial<AuthUser>) => void;
  signOut: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      tenant: null,
      user: null,
      role: null,
      signIn: ({ accessToken, tenant, user, role }) =>
        set({ accessToken, tenant, user, role }),
      setTenant: (tenant) => set({ tenant }),
      setMe: ({ user, tenant, role }) => set({ user, tenant, role }),
      patchUser: (patch) =>
        set((s) => (s.user ? { user: { ...s.user, ...patch } } : {})),
      signOut: () => {
        // The journey trail belongs to the session — a different user in the
        // same tab must not inherit the previous user's navigation state.
        clearNavTrail();
        set({ accessToken: null, tenant: null, user: null, role: null });
      },
    }),
    {
      name: 'seo.web.auth',
      partialize: (state) => ({
        accessToken: state.accessToken,
        tenant: state.tenant,
        user: state.user,
        role: state.role,
      }),
    },
  ),
);
